import moment from "moment";
import { createSelector } from "reselect";

import type { PollService } from "@busbud/core-types";

import { getZoneFromOffset } from "@app/helpers/date-helper";
import { isReturn } from "@app/helpers/search";
import { getSearchedLeg } from "@app/helpers/udm";
import { getDisplayPrice } from "@app/helpers/udm/get-display-price";
import type { Features } from "@app/types/experiments";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { SearchInfo } from "@app/types/search-types";

import { getCuratedTrips } from "./get-curated-trips";
import { getDisplayedPriceConfig } from "../get-displayed-price-config";
import { getConfig, getSearch, getTripsSegments } from "../state-accessors";

type LegHash = string;
type TripId = string;
export type LegCard = TripId[];
export type LegCards = Record<LegHash, LegCard>;

export function buildLegCardId(
  operator_id: string,
  origin_id: number | string,
  destination_id: number | string,
  /** ISO-8601 with tz  */
  departure_time: moment.Moment,
  /** ISO-8601 with tz */
  arrival_time: moment.Moment | undefined,
  origin_timezone: string,
  destination_timezone: string | undefined,
  experiments: Features | Partial<Features>
): string {
  if (experiments?.ENABLE_SHUTTLE_CARD && !experiments.SEAT_SELECTION_FIRST) {
    return `${operator_id}${origin_id}${departure_time.tz(origin_timezone).format()}`;
  } else {
    return `${operator_id}${origin_id}${destination_id}${departure_time.tz(origin_timezone).format()}${arrival_time?.tz(destination_timezone ?? origin_timezone).format()}`;
  }
}

interface LegCardIdData {
  operator_id: string;
  departure_time: string;
  origin_timezone: string;
  arrival_time: string | undefined;
  destination_timezone: string | undefined;
}

export function extractLegCardIdData(leg_card_id: string): LegCardIdData {
  const operator_id = leg_card_id.match(
    /^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/
  )?.[0];

  // Origin and destination ids are variable length so cannot be extracted

  const [departure_time, arrival_time] =
    leg_card_id
      .slice(36)
      .match(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}-\d{2}:00)/g) || [];

  const origin_tz_offset: string | undefined = departure_time?.slice(-6);
  const destination_tz_offset: string | undefined = arrival_time?.slice(-6);

  if (!operator_id || !departure_time || !departure_time || !origin_tz_offset) {
    throw new Error("Invalid leg card id");
  }

  // Get similar timezones based on the offsets
  const origin_timezone = getZoneFromOffset(origin_tz_offset)[0];
  const destination_timezone = destination_tz_offset
    ? getZoneFromOffset(destination_tz_offset)[0]
    : undefined;

  if (!origin_timezone || (destination_tz_offset && !destination_timezone)) {
    throw new Error("Invalid leg card id");
  }

  return {
    operator_id,
    departure_time,
    origin_timezone,
    arrival_time,
    destination_timezone
  };
}

function legCardIdFromSegments(
  origin:
    | PollService.API.RelatedPublicSegment
    | PollService.API.RelatedPublicSegmentV4,
  destination:
    | PollService.API.RelatedPublicSegment
    | PollService.API.RelatedPublicSegmentV4,
  experiments: Features
): string {
  const { origin_id, departure_time, operator_id } = origin;
  const { destination_id, arrival_time } = destination;

  return buildLegCardId(
    operator_id,
    origin_id,
    destination_id,
    moment(departure_time.timestamp),
    arrival_time ? moment(arrival_time.timestamp) : undefined,
    departure_time.timezone,
    arrival_time?.timezone,
    experiments
  );
}

/**
 * Build a uniq id for similar trips
 *
 * @param trip
 * @param segments
 * @param search
 * @param features
 * @param experiments
 */
export const getLegCardHashId = (
  trip: PollService.API.RelatedPublicTrip | PollService.API.RelatedPublicTripV4,
  segments: ResultsReduxState["trips_segments"],
  search: SearchInfo,
  experiments: Features
): LegHash => {
  const segment_ids = search && getSearchedLeg(search, trip).segment_ids;

  const [first_segment, last_segment] = [
    // @ts-expect-error TS2538 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    segments[segment_ids[0]],
    // @ts-expect-error TS2538 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    segments[segment_ids[segment_ids.length - 1]]
  ];

  return legCardIdFromSegments(first_segment, last_segment, experiments);
};

/**
 * Group similar trips but with different classes or fares into a LegCard item
 */
export const getLegCards = createSelector(
  [
    getConfig,
    getCuratedTrips,
    getTripsSegments,
    getSearch,
    getDisplayedPriceConfig
  ],
  (
    { experiments },
    { curated_trips },
    segments,
    search,
    displayed_price_config
  ): LegCards => {
    const direction = isReturn(search) ? "return" : "outbound";

    const byDisplayPrice = (a: string, b: string) => {
      const price_a = getDisplayPrice(
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        curated_trips[a],
        direction,
        displayed_price_config
      );
      const price_b = getDisplayPrice(
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        curated_trips[b],
        direction,
        displayed_price_config
      );

      // Ensure real roundtrips are prefered over fake roundtrips
      if (price_a === price_b) {
        // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        return curated_trips[a].prices.length < curated_trips[b].prices.length
          ? -1
          : 1;
      }

      return price_a < price_b ? -1 : 1;
    };

    const leg_cards: LegCards = Object.keys(curated_trips ?? {}).reduce(
      (acc, trip_id) => {
        const hash = getLegCardHashId(
          // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          curated_trips[trip_id],
          segments,
          search,
          experiments
        );

        acc[hash] = acc[hash] || [];
        acc[hash].push(trip_id);

        return acc;
      },
      {} as LegCards
    );

    // Sorts all trip ids by price
    Object.keys(leg_cards).forEach(key => {
      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      leg_cards[key].sort(byDisplayPrice);
    });

    return leg_cards;
  }
);
